// path: src/utils/achievements.js

import characters from "../Characters.json";
import {
    countUnlockedCatalogCards,
    migrateCollectionIds,
} from "./cardCatalog";
import {
    getStoryMainLevelCount,
    getTotalStoryLevels,
} from "../data/storyLevels";
import { hasCompletedStoryLevel } from "./storyProgress";

import {
    emptyBoardSizeMap,
    maxBoardSizeValue,
    normalizeBoardSizeMap,
} from "./leaderboards";

export const LS_ENDLESS_STATS_KEY = "mmu_endless_stats_v1";

export const GEM_CORGI_IDS = characters
    .filter((card) => card.rarity === "gem")
    .map((card) => Number(card.id));

export const ALPHA_CORGI_IDS = characters
    .filter((card) => card.rarity === "alpha")
    .map((card) => Number(card.id));

export const FOUNDER_CORGI_IDS = characters
    .filter((card) => card.rarity === "founder")
    .map((card) => Number(card.id));

export const SPECIAL_CORGI_IDS = [...GEM_CORGI_IDS, ...ALPHA_CORGI_IDS, ...FOUNDER_CORGI_IDS];

// Flipp, Johan, Ryan — first three founders in Characters.json order.
export const BROMANCE_CORGI_IDS = FOUNDER_CORGI_IDS.slice(0, 3);
// Flipp + Bella — first and fourth founders.
export const BESTIES_CORGI_IDS = [FOUNDER_CORGI_IDS[0], FOUNDER_CORGI_IDS[3]];

export const FOIL_TARGETS = [5, 10, 25];
export const HIGH_SCORE_TARGETS = [250, 500, 1250];
export const GAMES_PLAYED_TARGETS = [10, 50, 100];
export const STORY_WORLD_LEVELS = [3, 6, 9];

export const ENDLESS_STREAK_TARGETS = [
    50, 100, 150, 200, 250, 300, 350, 400, 450, 500,
];
export const ENDLESS_LEVEL_TARGETS = [
    10, 20, 30, 40, 50, 60, 70, 80, 90, 100,
];
export const ENDLESS_MODIFIER_LEVEL_TARGET = 10;

/** Admin / unlock-all targets covering achievements and streak cosmetics. */
export const ADMIN_UNLOCK_ENDLESS_STREAK = 500;
export const ADMIN_UNLOCK_ENDLESS_LEVEL = Math.max(...ENDLESS_LEVEL_TARGETS);
export const ADMIN_UNLOCK_HIGH_SCORE = Math.max(...HIGH_SCORE_TARGETS);
export const ADMIN_UNLOCK_GAMES_PLAYED = Math.max(...GAMES_PLAYED_TARGETS);

export const ENDLESS_BOARD_LABELS = {
    4: "2×2",
    9: "3×3",
    16: "4×4",
};

export const ENDLESS_MODIFIER_ACHIEVEMENTS = [
    {
        key: "twistedMode",
        id: "endless-modifier-twisted",
        title: "Spin Cycle",
        description:
            "Reach Endless level 10 with Twisted Mode enabled.",
    },
    {
        key: "shyCorgisMode",
        id: "endless-modifier-shy",
        title: "Camera Shy",
        description:
            "Reach Endless level 10 with Shy Corgis enabled.",
    },
    {
        key: "hardcoreMode",
        id: "endless-modifier-hardcore",
        title: "No Second Chances",
        description:
            "Reach Endless level 10 with Hardcore enabled.",
    },
    {
        key: "blitzMode",
        id: "endless-modifier-blitz",
        title: "Beat the Clock",
        description: "Reach Endless level 10 with Blitz enabled.",
    },
];

const MODIFIER_KEYS = ENDLESS_MODIFIER_ACHIEVEMENTS.map((item) => item.key);

const normalizeId = (id) => {
    const n = Number(id);
    return Number.isFinite(n) ? n : id;
};

const toIdSet = (ids = []) =>
    new Set((Array.isArray(ids) ? ids : []).map(normalizeId));

const emptyModifierLevels = () => ({
    twistedMode: 0,
    shyCorgisMode: 0,
    anxiousMode: 0,
    hardcoreMode: 0,
    blitzMode: 0,
});

export function createEmptyEndlessStats() {
    return {
        version: 3,
        hasPlayedEndless: false,
        bestPerfectStreak: 0,
        bestLevelByBoardSize: emptyBoardSizeMap(),
        bestBlitzLevelByBoardSize: emptyBoardSizeMap(),
        bestPerfectStreakByBoardSize: emptyBoardSizeMap(),
        bestScoreByBoardSize: emptyBoardSizeMap(),
        bestLevelWithModifier: emptyModifierLevels(),
        dontCallItAComeback: false,
    };
}

/**
 * Admin unlock-all: max Endless stats so achievements and streak cosmetics
 * evaluate as complete.
 */
export function buildAdminUnlockAllEndlessStats() {
    const bestLevelByBoardSize = emptyBoardSizeMap();
    const bestBlitzLevelByBoardSize = emptyBoardSizeMap();
    const bestPerfectStreakByBoardSize = emptyBoardSizeMap();
    const bestScoreByBoardSize = emptyBoardSizeMap();
    const bestLevelWithModifier = emptyModifierLevels();

    [4, 9, 16].forEach((size) => {
        bestLevelByBoardSize[size] = ADMIN_UNLOCK_ENDLESS_LEVEL;
        bestBlitzLevelByBoardSize[size] = ADMIN_UNLOCK_ENDLESS_LEVEL;
        bestPerfectStreakByBoardSize[size] = ADMIN_UNLOCK_ENDLESS_STREAK;
        bestScoreByBoardSize[size] = ADMIN_UNLOCK_HIGH_SCORE;
    });

    Object.keys(bestLevelWithModifier).forEach((key) => {
        bestLevelWithModifier[key] = ENDLESS_MODIFIER_LEVEL_TARGET;
    });

    return normalizeEndlessStats({
        hasPlayedEndless: true,
        bestPerfectStreak: ADMIN_UNLOCK_ENDLESS_STREAK,
        bestLevelByBoardSize,
        bestBlitzLevelByBoardSize,
        bestPerfectStreakByBoardSize,
        bestScoreByBoardSize,
        bestLevelWithModifier,
        dontCallItAComeback: true,
    });
}

export function normalizeEndlessStats(raw = null) {
    const empty = createEmptyEndlessStats();
    if (!raw || typeof raw !== "object") {
        return empty;
    }

    const bestLevelByBoardSize = normalizeBoardSizeMap(
        raw.bestLevelByBoardSize
    );
    const bestBlitzLevelByBoardSize = normalizeBoardSizeMap(
        raw.bestBlitzLevelByBoardSize
    );
    const bestPerfectStreakByBoardSize = normalizeBoardSizeMap(
        raw.bestPerfectStreakByBoardSize
    );
    const bestScoreByBoardSize = normalizeBoardSizeMap(raw.bestScoreByBoardSize);

    // Migrate legacy single streak onto unknown board buckets is impossible;
    // keep global streak and per-board map independently.
    const bestLevelWithModifier = emptyModifierLevels();
    const rawModifiers =
        raw.bestLevelWithModifier && typeof raw.bestLevelWithModifier === "object"
            ? raw.bestLevelWithModifier
            : {};

    MODIFIER_KEYS.forEach((key) => {
        const value = Number(rawModifiers[key] ?? 0);
        bestLevelWithModifier[key] = Number.isFinite(value)
            ? Math.max(0, Math.floor(value))
            : 0;
    });

    // Seed blitz-per-board from legacy aggregate when per-board is empty.
    const legacyBlitz = Number(bestLevelWithModifier.blitzMode) || 0;
    const hasPerBoardBlitz = Object.values(bestBlitzLevelByBoardSize).some(
        (value) => value > 0
    );
    if (!hasPerBoardBlitz && legacyBlitz > 0) {
        // Unknown board for legacy blitz — do not invent a board assignment.
    }

    const bestPerfectStreakRaw = Number(raw.bestPerfectStreak);
    const bestPerfectStreak = Number.isFinite(bestPerfectStreakRaw)
        ? Math.max(0, Math.floor(bestPerfectStreakRaw))
        : 0;

    return {
        version: 3,
        hasPlayedEndless: Boolean(raw.hasPlayedEndless),
        bestPerfectStreak: Math.max(
            bestPerfectStreak,
            maxBoardSizeValue(bestPerfectStreakByBoardSize)
        ),
        bestLevelByBoardSize,
        bestBlitzLevelByBoardSize,
        bestPerfectStreakByBoardSize,
        bestScoreByBoardSize,
        bestLevelWithModifier,
        dontCallItAComeback: Boolean(raw.dontCallItAComeback),
    };
}

export function mergeEndlessStats(localStats, remoteStats) {
    const local = normalizeEndlessStats(localStats);
    const remote = normalizeEndlessStats(remoteStats);

    const bestLevelWithModifier = emptyModifierLevels();
    MODIFIER_KEYS.forEach((key) => {
        bestLevelWithModifier[key] = Math.max(
            local.bestLevelWithModifier[key],
            remote.bestLevelWithModifier[key]
        );
    });

    const bestLevelByBoardSize = emptyBoardSizeMap();
    const bestBlitzLevelByBoardSize = emptyBoardSizeMap();
    const bestPerfectStreakByBoardSize = emptyBoardSizeMap();
    const bestScoreByBoardSize = emptyBoardSizeMap();

    [4, 9, 16].forEach((size) => {
        bestLevelByBoardSize[size] = Math.max(
            local.bestLevelByBoardSize[size],
            remote.bestLevelByBoardSize[size]
        );
        bestBlitzLevelByBoardSize[size] = Math.max(
            local.bestBlitzLevelByBoardSize[size],
            remote.bestBlitzLevelByBoardSize[size]
        );
        bestPerfectStreakByBoardSize[size] = Math.max(
            local.bestPerfectStreakByBoardSize[size],
            remote.bestPerfectStreakByBoardSize[size]
        );
        bestScoreByBoardSize[size] = Math.max(
            local.bestScoreByBoardSize[size],
            remote.bestScoreByBoardSize[size]
        );
    });

    return {
        version: 3,
        hasPlayedEndless: local.hasPlayedEndless || remote.hasPlayedEndless,
        bestPerfectStreak: Math.max(
            local.bestPerfectStreak,
            remote.bestPerfectStreak,
            maxBoardSizeValue(bestPerfectStreakByBoardSize)
        ),
        bestLevelByBoardSize,
        bestBlitzLevelByBoardSize,
        bestPerfectStreakByBoardSize,
        bestScoreByBoardSize,
        bestLevelWithModifier,
        dontCallItAComeback:
            local.dontCallItAComeback || remote.dontCallItAComeback,
    };
}

export function loadEndlessStats() {
    try {
        const raw = localStorage.getItem(LS_ENDLESS_STATS_KEY);
        if (!raw) {
            return createEmptyEndlessStats();
        }
        return normalizeEndlessStats(JSON.parse(raw));
    } catch (error) {
        return createEmptyEndlessStats();
    }
}

export function saveEndlessStats(stats) {
    try {
        localStorage.setItem(
            LS_ENDLESS_STATS_KEY,
            JSON.stringify(normalizeEndlessStats(stats))
        );
    } catch (error) {
        // Ignore quota / private-mode failures.
    }
}

function countOwnedInList(unlockedIds, requiredIds) {
    const owned = toIdSet(unlockedIds);
    return requiredIds.filter((id) => owned.has(normalizeId(id))).length;
}

function buildCollectionMilestones() {
    // Corgi Collector: 10 tiers × 50 cards (T1=50 … T10=500).
    // Legacy obtain-10…obtain-40 ids were retired; do not restore them.
    return Array.from({ length: 10 }, (_, index) => {
        const target = (index + 1) * 50;
        return {
            id: `obtain-${target}-corgi-cards`,
            title: `Obtain ${target} Corgi Cards`,
            description: `Unlock ${target} cards in your collection.`,
            category: "collection",
            kind: "count",
            target,
            groupId: "collection-milestones",
            groupTitle: "Corgi Collector",
            groupDescription: "Unlock cards in your collection.",
            getProgress: ({ unlockedCount }) => unlockedCount,
        };
    });
}

const STORY_WORLD_TITLES = {
    3: "Trailblazer",
    6: "Pathfinder",
    9: "Summit Seeker",
};

export const ACHIEVEMENTS = [
    ...buildCollectionMilestones(),
    {
        id: "obtain-all-corgi-cards",
        title: "Overachiever",
        description: "Unlock every Corgi card in the collection.",
        category: "collection",
        kind: "count",
        target: characters.length,
        getProgress: ({ unlockedCount }) => unlockedCount,
    },
    {
        id: "first-foil",
        title: "Shiny!",
        description: "Find your first foil card.",
        category: "collection",
        kind: "count",
        target: 1,
        groupId: "collection-foils",
        groupTitle: "Foil Hunter",
        groupDescription: "Find foil cards in your collection.",
        getProgress: ({ foilCount }) => foilCount,
    },
    ...FOIL_TARGETS.map((target) => ({
        id: `foil-${target}`,
        title: `${target} Foils`,
        description: `Find ${target} foil cards.`,
        category: "collection",
        kind: "count",
        target,
        groupId: "collection-foils",
        groupTitle: "Foil Hunter",
        groupDescription: "Find foil cards in your collection.",
        getProgress: ({ foilCount }) => foilCount,
    })),
    {
        id: "hidden-gems",
        title: "Hidden Gems",
        description: "Collect every Gem Corgi.",
        category: "collection",
        kind: "count",
        target: GEM_CORGI_IDS.length,
        getProgress: ({ collectionUnlocked }) =>
            countOwnedInList(collectionUnlocked, GEM_CORGI_IDS),
    },
    {
        id: "alpha-edition",
        title: "Alpha Edition",
        description: "Collect all 8 Alpha Edition Corgis.",
        category: "collection",
        kind: "count",
        target: ALPHA_CORGI_IDS.length,
        getProgress: ({ collectionUnlocked }) =>
            countOwnedInList(collectionUnlocked, ALPHA_CORGI_IDS),
    },
    {
        id: "bromance",
        title: "Bromance",
        description:
            "Collect Flipp the Founder, Johan the Viking, and Ryan the Patriot.",
        category: "collection",
        kind: "count",
        hidden: true,
        target: BROMANCE_CORGI_IDS.length,
        getProgress: ({ collectionUnlocked }) =>
            countOwnedInList(collectionUnlocked, BROMANCE_CORGI_IDS),
    },
    {
        id: "besties",
        title: "Besties",
        description: "Collect Flipp the Founder and Bella the Infinity.",
        category: "collection",
        kind: "count",
        hidden: true,
        target: BESTIES_CORGI_IDS.length,
        getProgress: ({ collectionUnlocked }) =>
            countOwnedInList(collectionUnlocked, BESTIES_CORGI_IDS),
    },
    ...STORY_WORLD_LEVELS.map((levelId) => ({
        id: `story-level-${levelId}`,
        title: STORY_WORLD_TITLES[levelId] || `Story Level ${levelId}`,
        description: `Clear Story Level ${levelId}.`,
        category: "story",
        kind: "boolean",
        target: 1,
        groupId: "story-world-levels",
        groupTitle: "Story Explorer",
        groupDescription: "Clear milestone Story levels.",
        getProgress: ({ storyProgress }) =>
            hasCompletedStoryLevel(levelId, storyProgress) ? 1 : 0,
    })),
    {
        id: "story-complete",
        title: "Story Complete",
        description: "Clear Story Level 12 and finish the Overworld.",
        category: "story",
        kind: "boolean",
        target: 1,
        getProgress: ({ storyProgress }) =>
            hasCompletedStoryLevel(getStoryMainLevelCount(), storyProgress)
                ? 1
                : 0,
    },
    {
        id: "underworld-opened",
        title: "Underworld Opened",
        description: "Unlock the Underworld by clearing Story Level 12.",
        category: "story",
        kind: "boolean",
        target: 1,
        getProgress: ({ storyProgress }) =>
            hasCompletedStoryLevel(getStoryMainLevelCount(), storyProgress)
                ? 1
                : 0,
    },
    {
        id: "underworld-finale",
        title: "Underworld Finale",
        description: `Clear Story Level ${getTotalStoryLevels()}, The Final Flame.`,
        category: "story",
        kind: "boolean",
        target: 1,
        getProgress: ({ storyProgress }) =>
            hasCompletedStoryLevel(getTotalStoryLevels(), storyProgress)
                ? 1
                : 0,
    },
    {
        id: "dont-call-it-a-comeback",
        title: "Don't Call It a Comeback",
        description:
            "Spend 3+ hearts on a single 4×4 Story level and still clear it.",
        category: "story",
        kind: "boolean",
        target: 1,
        getProgress: ({ endlessStats }) =>
            endlessStats?.dontCallItAComeback ? 1 : 0,
    },
    {
        id: "first-endless",
        title: "Endless Beginnings",
        description: "Start your first Endless Mode run.",
        category: "endless",
        kind: "boolean",
        target: 1,
        getProgress: ({ endlessStats }) =>
            endlessStats?.hasPlayedEndless ? 1 : 0,
    },
    ...ENDLESS_STREAK_TARGETS.map((target) => ({
        id: `endless-perfect-streak-${target}`,
        title: `Perfect Streak ${target}`,
        description: `Reach a perfect correct streak of ${target} in Endless Mode.`,
        category: "endless",
        kind: "count",
        target,
        groupId: "endless-perfect-streak",
        groupTitle: "Perfect Streak",
        groupDescription:
            "Reach a perfect correct streak in Endless Mode.",
        getProgress: ({ endlessStats }) =>
            Number(endlessStats?.bestPerfectStreak) || 0,
    })),
    ...[4, 9, 16].flatMap((boardSize) =>
        ENDLESS_LEVEL_TARGETS.map((target) => ({
            id: `endless-${boardSize}-level-${target}`,
            title: `${ENDLESS_BOARD_LABELS[boardSize]} Level ${target}`,
            description: `Reach level ${target} on an Endless ${ENDLESS_BOARD_LABELS[boardSize]} board.`,
            category: "endless",
            kind: "count",
            target,
            groupId: `endless-board-${boardSize}-levels`,
            groupTitle: `${ENDLESS_BOARD_LABELS[boardSize]} Endless Levels`,
            groupDescription: `Reach higher levels on an Endless ${ENDLESS_BOARD_LABELS[boardSize]} board.`,
            getProgress: ({ endlessStats }) =>
                Number(endlessStats?.bestLevelByBoardSize?.[boardSize]) || 0,
        }))
    ),
    ...ENDLESS_MODIFIER_ACHIEVEMENTS.map((modifier) => ({
        id: modifier.id,
        title: modifier.title,
        description: modifier.description,
        category: "endless",
        kind: "count",
        target: ENDLESS_MODIFIER_LEVEL_TARGET,
        getProgress: ({ endlessStats }) =>
            Number(endlessStats?.bestLevelWithModifier?.[modifier.key]) || 0,
    })),
    ...HIGH_SCORE_TARGETS.map((target) => ({
        id: `high-score-${target}`,
        title: `High Score ${target}`,
        description: `Reach a best Endless score of ${target}.`,
        category: "endless",
        kind: "count",
        target,
        groupId: "endless-high-score",
        groupTitle: "High Score",
        groupDescription: "Reach a best Endless score.",
        getProgress: ({ topScore }) => Number(topScore) || 0,
    })),
    ...GAMES_PLAYED_TARGETS.map((target) => ({
        id: `games-played-${target}`,
        title: `${target} Games`,
        description: `Play ${target} games.`,
        category: "volume",
        kind: "count",
        target,
        groupId: "volume-games-played",
        groupTitle: "Games Played",
        groupDescription: "Play games across Story and Endless.",
        getProgress: ({ gamesPlayed }) => Number(gamesPlayed) || 0,
    })),
];

export const ACHIEVEMENT_CATEGORIES = [
    { id: "collection", label: "Collection" },
    { id: "story", label: "Story" },
    { id: "endless", label: "Endless" },
    { id: "volume", label: "Play volume" },
];

export function evaluateAchievement(achievement, context) {
    const current = Math.max(0, Number(achievement.getProgress(context)) || 0);
    const target = Math.max(1, Number(achievement.target) || 1);
    const progressValue = Math.min(current, target);
    const completed = current >= target;
    const percent = Math.min(100, Math.round((progressValue / target) * 100));
    const isSecret = Boolean(achievement.hidden) && !completed;

    return {
        ...achievement,
        current,
        progressValue,
        percent,
        completed,
        isSecret,
        displayTitle: isSecret ? "?????" : achievement.title,
        displayDescription: isSecret
            ? "A secret achievement. Keep collecting to discover it."
            : achievement.description,
    };
}

export function evaluateAchievements(context) {
    return ACHIEVEMENTS.map((achievement) =>
        evaluateAchievement(achievement, context)
    );
}

function buildAchievementGroupDisplayItem(tiers, headAchievement) {
    const sortedTiers = [...tiers].sort(
        (left, right) => Number(left.target) - Number(right.target)
    );
    const completedTiers = sortedTiers.filter((tier) => tier.completed);
    const nextTier = sortedTiers.find((tier) => !tier.completed) || null;
    const current = Math.max(0, Number(sortedTiers[0]?.current) || 0);
    const completedCount = completedTiers.length;
    const totalCount = sortedTiers.length;
    const completed = completedCount === totalCount;
    const progressTarget = nextTier
        ? Math.max(1, Number(nextTier.target) || 1)
        : Math.max(
              1,
              Number(sortedTiers[sortedTiers.length - 1]?.target) || 1
          );
    const progressValue = nextTier
        ? Math.min(Number(nextTier.current) || 0, progressTarget)
        : progressTarget;
    const percent = Math.min(
        100,
        Math.round((progressValue / progressTarget) * 100)
    );

    return {
        type: "group",
        id: headAchievement.groupId,
        groupTitle: headAchievement.groupTitle || headAchievement.title,
        groupDescription:
            headAchievement.groupDescription || headAchievement.description,
        category: headAchievement.category,
        current,
        completed,
        completedCount,
        totalCount,
        nextTier,
        progressTarget,
        progressValue,
        percent,
        tiers: sortedTiers.map((tier, index) => ({
            tierLabel: `T${index + 1}`,
            tierIndex: index + 1,
            target: tier.target,
            completed: tier.completed,
            title: tier.title,
            description: tier.description,
            id: tier.id,
        })),
    };
}

export function buildAchievementDisplayItems(evaluated = []) {
    const seenGroups = new Set();
    const displayItems = [];

    evaluated.forEach((achievement) => {
        if (achievement.groupId) {
            if (seenGroups.has(achievement.groupId)) {
                return;
            }
            seenGroups.add(achievement.groupId);
            const tiers = evaluated.filter(
                (item) => item.groupId === achievement.groupId
            );
            displayItems.push(
                buildAchievementGroupDisplayItem(tiers, achievement)
            );
            return;
        }

        displayItems.push({
            type: "single",
            id: achievement.id,
            achievement,
        });
    });

    return displayItems;
}

export function getAchievementSummary(context) {
    const evaluated = evaluateAchievements(context);
    const completedCount = evaluated.filter((item) => item.completed).length;
    const sections = ACHIEVEMENT_CATEGORIES.map((category) => {
        const items = evaluated.filter(
            (item) => item.category === category.id
        );
        return {
            id: category.id,
            label: category.label,
            items,
            displayItems: buildAchievementDisplayItems(items),
            completedCount: items.filter((item) => item.completed).length,
            totalCount: items.length,
        };
    }).filter((section) => section.totalCount > 0);

    return {
        evaluated,
        sections,
        completedCount,
        totalCount: evaluated.length,
    };
}

export function buildAchievementContext({
    collectionUnlocked = [],
    collectionFoilUnlocked = [],
    storyProgress = null,
    endlessStats = null,
    topScore = 0,
    gamesPlayed = 0,
    totalCards = characters.length,
} = {}) {
    const normalizedUnlocked = migrateCollectionIds(collectionUnlocked);
    const normalizedFoils = migrateCollectionIds(collectionFoilUnlocked);

    return {
        collectionUnlocked: normalizedUnlocked,
        collectionFoilUnlocked: normalizedFoils,
        unlockedCount: countUnlockedCatalogCards(normalizedUnlocked, characters),
        foilCount: countUnlockedCatalogCards(normalizedFoils, characters),
        storyProgress,
        endlessStats: normalizeEndlessStats(endlessStats),
        topScore: Number(topScore) || 0,
        gamesPlayed: Number(gamesPlayed) || 0,
        totalCards,
    };
}
